// #region Latest Pipeline Context Contract

import { IPipelineCopilotResponseCore, PipelineCopilotErrorCode, TridentGetPipelineCopilotResponseParams } from './TridentApiServiceDefinitions';

/**
 * @see https://dev.azure.com/msdata/PROSE/_git/PROSE?path=/Copilot/Copilot.FabricPipeline/Prompts/Resources/Pipeline.ts
 */
export enum DependencyConditionContext {
    Success = 'Success',
    Failure = 'Failure',
    Completion = 'Completion',
    Skipped = 'Skipped'
}

export enum ParameterVariableTypeContext {
    // #region Parameter
    String = 'String',
    Int = 'Int',
    Float = 'Float',
    Bool = 'Bool',
    Array = 'Array',
    Object = 'Object',
    SecureString = 'SecureString',
    // #endregion
    // #region Variable
    Boolean = 'Boolean',
    Integer = 'Integer'
    // #endregion
}

export type IConnectionContext = IKnownConnectionContext | IUnknownConnectionContext;

export interface IKnownConnectionContext {
    Name: string;
    Type: string;
}

export interface IUnknownConnectionContext {
    Name: string;
    Type: 'Unknown';
    OriginalConnectionType: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IComputeConnectionContext extends IKnownConnectionContext {}

export interface IDatabaseConnectionLocationContext {
    Database?: string;
    Schema?: string;
    Table?: string;
    Query?: string;
}

export interface IDatabaseConnectionContext extends IKnownConnectionContext, IDatabaseConnectionLocationContext {}

export interface IFileConnectionLocationContext {
    Container?: string;
    Filename?: string;
    FolderPath?: string;
}

export interface IFileSystemConnectionContext extends IKnownConnectionContext, IFileConnectionLocationContext {}

export interface IHttpConnectionLocationContext {
    Url?: string;
}

export interface IHttpConnectionContext extends IKnownConnectionContext, IHttpConnectionLocationContext {}

export interface IKnownActivityContext<T extends string = string> {
    Name: string;
    Type: T;
    Dependencies: IDependencyContext[];
    State?: 'Activated' | 'Deactivated';
    Timeout?: string;
    RetryCount?: number;
}

export interface IUnknownActivityContext<T extends string = string> {
    Name: string;
    Type: 'Unknown';
    OriginalActivityType?: T;
    Dependencies: IDependencyContext[];
}

export type IActivityContext<T extends string = string> = IKnownActivityContext<T> | IUnknownActivityContext<T>;

export type IGenericActivityContext = IActivityContext<
    | 'Office365Email'
>;

export type ActivityConnectionContext =
    | IDatabaseConnectionContext
    | IFileSystemConnectionContext
    | IHttpConnectionContext
    | IUnknownConnectionContext;

export type ActivityConnectionLocationContext =
    | IDatabaseConnectionLocationContext
    | IFileConnectionLocationContext
    | IHttpConnectionLocationContext
    | IUnknownConnectionContext;

export interface IAppendVariableActivityContext extends IKnownActivityContext<'AppendVariable'> {
    VariableName?: string;
    VariableValue?: string;
}

export interface IAzureBatchActivityContext extends IKnownActivityContext<'Custom'> {
    Command: string;
    Connection?: IComputeConnectionContext;
    ResourceConnection?: IFileSystemConnectionContext;
}

export interface IAzureFunctionActivityContext extends IKnownActivityContext<'AzureFunctionActivity'> {
    Connection?: IComputeConnectionContext;
}

export interface IAzureHdInsightActivityContext extends IKnownActivityContext<'AzureHDInsight'> {
    HdInsightType?: 'HDInsightHive' | 'HDInsightMapReduce' | 'HDInsightPig' | 'HDInsightSpark' | 'HDInsightStreaming';
    ScriptFilePath: string;
    Connection?: IComputeConnectionContext;
}

export interface IAzureMlExecutePipelineActivityContext extends IKnownActivityContext<'AzureMLExecutePipeline'> {
    EndpointType?: 'BatchEndpoint' | 'PipelineV1';
    Connection?: IComputeConnectionContext;
}

export interface ICopyActivityContext extends IKnownActivityContext<'Copy'> {
    SourceConnection?: ActivityConnectionContext;
    TargetConnection?: ActivityConnectionContext;
}

export interface IDatabricksNotebookActivityContext extends IKnownActivityContext<'DatabricksNotebook'> {
    Connection?: IComputeConnectionContext;
}

export interface IDataLakeAnalyticsScopeActivityContext extends IKnownActivityContext<'DataLakeAnalyticsScope'> {
    ScopeScriptPath: string;
    ScopeScriptFilename: string;
    TestProcessIsolation?: boolean;
    Connection?: IComputeConnectionContext;
}

export interface IDeleteActivityContext extends IKnownActivityContext<'Delete'> {
    Connection?: IFileSystemConnectionContext;
    Recursive?: string;
}

export interface IFilterActivityContext extends IKnownActivityContext<'Filter'> {
    ItemsExpression: string;
    ConditionExpression: string;
}

export interface IFabricSparkJobDefinitionActivityContext extends IKnownActivityContext<'FabricSparkJobDefinition'> {
    WorkspaceName: string;
    WorkspaceId: string;
    JobDefinitionName: string;
    JobDefinitionId: string;
    Connection?: ActivityConnectionContext;
}

export interface IFailActivityContext extends IKnownActivityContext<'Fail'> {
    FailureMessage: string;
    FailureErrorCode: string;
}

export interface IForEachActivityContext extends IKnownActivityContext<'ForEach'> {
    ItemExpression?: string;
    ChildActivityNames?: string[];
    IsSequential?: boolean;
    BatchCount?: number;
}

export interface IGetMetadataActivityContext extends IKnownActivityContext<'GetMetadata'> {
    Connection?: ActivityConnectionContext;
}

export interface IIfConditionActivityContext extends IKnownActivityContext<'IfCondition'> {
    Expression?: string;
    TrueChildActivityNames?: string[];
    FalseChildActivityNames?: string[];
}

// Legacy ExecutePipelineActivity
export interface IExecutePipelineActivityContext extends IKnownActivityContext<'ExecutePipeline'> {
    PipelineId: string;
    PipelineName: string;
    WaitOnCompletion?: boolean;
}

// New InvokePipelineActivity
export interface IInvokePipelineActivityContext extends IKnownActivityContext<'InvokePipeline'> {
    Connection?: ActivityConnectionContext;
    PipelineType?: 'Fabric' | 'AzureDataFactory' | 'Synapse';
    WorkspaceId: string;
    WorkspaceName: string;
    PipelineId: string;
    PipelineName: string;
    WaitOnCompletion?: boolean;
}

export interface IKustoQueryLanguageActivityContext extends IKnownActivityContext<'KustoQueryLanguage'> {
    Command: string;
    Connection?: IDatabaseConnectionContext;
}

export interface ILookupActivityContext extends IKnownActivityContext<'Lookup'> {
    Connection?: ActivityConnectionContext;
    FirstRowOnly?: string;
}

export interface IOffice365OutlookActivityContext extends IKnownActivityContext<'Office365Outlook'> {
    ToEmailAddress: string;
    Importance?: 'Low' | 'Normal' | 'High';
    Subject: string;
    Body: string;
    FromEmailAddress?: string;
    CarbonCopyEmailAddress?: string;
    BlindCarbonCopyEmailAddress?: string;
    ReplyToEmailAddress?: string;
}

export interface IPbiSemanticModelRefreshActivityContext extends IKnownActivityContext<'PBISemanticModelRefresh'> {
    WorkspaceName: string;
    WorkspaceId: string;
    SemanticModelName: string;
    SemanticModelId: string;
    TableNames: string[];
    Connection?: IHttpConnectionContext;
}

export interface IRefreshDataflowActivityContext extends IKnownActivityContext<'RefreshDataflow'> {
    WorkspaceName: string;
    WorkspaceId: string;
    DataflowName: string;
    DataflowId: string;
}

export interface ISqlServerStoredProcedureActivityContext extends IKnownActivityContext<'SqlServerStoredProcedure'> {
    Connection?: IDatabaseConnectionContext;
    ProcedureName?: string;
}

export interface IScriptContext {
    Type: 'Query' | 'NonQuery';
    Body: string;
}

export interface IScriptActivityContext extends IKnownActivityContext<'Script'> {
    Connection?: IDatabaseConnectionContext;
    Scripts: IScriptContext[];
}

export interface IVariableContext {
    Name: string;
    Value: string;
    Type: string;
}

export interface ISetVariableActivityContext extends IKnownActivityContext<'SetVariable'> {
    VariableType: 'PipelineVariable' | 'PipelineReturnValue';
    Variable?: IVariableContext;
    ReturnVariables?: IVariableContext[];
}

export interface ISwitchCaseContext {
    ExpressionReturnValue?: string;
    ChildActivityNames?: string[];
}

export interface ISwitchActivityContext extends IKnownActivityContext<'Switch'> {
    Expression?: string;
    Cases?: ISwitchCaseContext[];
    DefaultChildActivityNames?: string[];
}

export interface ITeamsActivityContext extends IKnownActivityContext<'Teams'> {
    TeamName?: string;
    TeamId?: string;
    MessageBody?: string;
    MessageType?: 'GroupChat' | 'Channel';
    ChannelName?: string;
    ChannelId?: string;
    GroupChatName?: string;
    GroupChatId?: string;
}

export interface ITridentNotebookActivityContext extends IKnownActivityContext<'TridentNotebook'> {
    WorkspaceName: string;
    WorkspaceId: string;
    NotebookName: string;
    NotebookId: string;
}

export interface IInvokeCopyJobActivityContext extends IKnownActivityContext<'InvokeCopyJob'> {
    WorkspaceName: string;
    WorkspaceId: string;
    CopyJobName: string;
    CopyJobId: string;
}

export interface IInvokeDataBuildToolJobActivityContext extends IKnownActivityContext<'InvokeDataBuildToolJob'> {
    WorkspaceName: string;
    WorkspaceId: string;
    DataBuildToolJobName: string;
    DataBuildToolJobId: string;
}

export interface IInvokeDataQualityActivityContext extends IKnownActivityContext<'InvokeDataQuality'> {
    WorkspaceName: string;
    WorkspaceId: string;
    DataQualityId: string;
}

export interface IMicrosoftTeamsActivityContext extends IKnownActivityContext<'MicrosoftTeams'> {
    ChatId?: string;
}

export interface IApiIntegrationActivityContext extends IKnownActivityContext<'ApiIntegration'> {
    ServiceType?: string;
    WorkspaceId: string;
    ItemType: string;
    ItemId: string;
    ApiRequest: string;
}

export interface IRMLVActivityContext extends IKnownActivityContext<'RefreshMaterializedLakeView'> {
    LakehouseId?: string;
}

export interface ILHMActivityContext extends IKnownActivityContext<'LakehouseMaintenance'> {
    Table?: string;
    LakehouseId?: string;
}

export interface IWebActivityContext extends IKnownActivityContext<'WebActivity'> {
    Connection?: IHttpConnectionContext;
    RelativeUrl?: string;
    Method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    Headers?: Record<string, string>;
    Body?: string;
    Timeout?: string;
}

export interface IWaitActivityContext extends IKnownActivityContext<'Wait'> {
    WaitSeconds?: string;
}

export interface IWebHookActivityContext extends IKnownActivityContext<'WebHook'> {
    Connection?: IHttpConnectionContext;
    RelativeUrl?: string;
    Method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    Headers?: Record<string, string>;
    Body?: string;
    Timeout?: string;
}

export interface IUntilActivityContext extends IKnownActivityContext<'Until'> {
    ChildActivityNames?: string[];
    StopExpression?: string;
    Timeout?: string;
}

export interface IDependencyContext {
    ActivityName: string;
    Conditions: DependencyConditionContext[];
}

export interface IPipelineParameterVariableContext {
    Name: string;
    Type: ParameterVariableTypeContext;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DefaultValue?: any;
}

export enum PipelineActionType {
    AddActivity = 'AddActivity',
    UpdateActivity = 'UpdateActivity',
    RemoveActivity = 'RemoveActivity',
    AddParameter = 'AddParameter',
    UpdateParameter = 'UpdateParameter',
    RemoveParameter = 'RemoveParameter',
    AddVariable = 'AddVariable',
    UpdateVariable = 'UpdateVariable',
    RemoveVariable = 'RemoveVariable'
}

type PipelineActivityContextBase =
    | IAppendVariableActivityContext
    | IAzureBatchActivityContext
    | IAzureFunctionActivityContext
    | IAzureHdInsightActivityContext
    | IAzureMlExecutePipelineActivityContext
    | ICopyActivityContext
    | IDataLakeAnalyticsScopeActivityContext
    | IFabricSparkJobDefinitionActivityContext
    | IFailActivityContext
    | IFilterActivityContext
    | IKustoQueryLanguageActivityContext
    | ILookupActivityContext
    | IDeleteActivityContext
    | IGetMetadataActivityContext
    | ITridentNotebookActivityContext
    | IWebActivityContext
    | IWebHookActivityContext
    | IRefreshDataflowActivityContext
    | ISqlServerStoredProcedureActivityContext
    | IScriptActivityContext
    | ISetVariableActivityContext
    | ITeamsActivityContext
    | IGenericActivityContext
    | IWaitActivityContext
    | IExecutePipelineActivityContext
    | IOffice365OutlookActivityContext
    | IPbiSemanticModelRefreshActivityContext
    | IInvokePipelineActivityContext
    | IDatabricksNotebookActivityContext;

export type PipelineActivityContext =
    | PipelineActivityContextBase
    | IForEachActivityContext
    | IIfConditionActivityContext
    | ISwitchActivityContext
    | IUntilActivityContext;

export interface IUpdatePipelinePayloadItem {
    Type: PipelineActionType;
    Parameter?: IPipelineParameterVariableContext;
    Variable?: IPipelineParameterVariableContext;
    Activity?: PipelineActivityContext;
}

export interface IUpdatePipelinePayload {
    payload: IUpdatePipelinePayloadItem[];
    pipelineContext: IPipelineCopilotCurrentPipelineContext<PipelineActivityContext[]>;
}

export interface IUpdateActivityDescriptionsPayload {
    activityDescriptions: { [activityName: string]: string };
    overwriteExisting: boolean;
}

export interface INavigationPayload {
    activityName?: string;
    fieldName?: string;
}

export interface IErrorItemContext {
    Message: string;
    Type: string;
    Code: string;
    ActivityNames: string[];
    ActivityType: string;
    DocLink?: string;
    Timestamp?: string;
}

export interface IRunContext {
    Id?: string;
    StartTime: string;
    EndTime: string;
    Status: string;
    Errors: IErrorItemContext[];
}

export interface IPipelineCopilotCurrentPipelineContext<A = PipelineActivityInterimContext[]> {
    Parameters?: IPipelineParameterVariableContext[];
    Variables?: IPipelineParameterVariableContext[];
    Activities?: A;
}

export interface IPipelineCopilotContext {
    Pipeline: IPipelineCopilotCurrentPipelineContext<PipelineActivityContext[]>;
    Runs: IRunContext[];
}
// #endregion

// #region Interim Pipeline Context Contract, referenced in model adapters.
export interface IForEachActivityInterimContext extends IKnownActivityContext<'ForEach'> {
    ItemExpression?: string;
    ChildActivities?: PipelineActivityInterimContext[];
    IsSequential?: boolean;
    BatchCount?: number;
}

export interface IIfConditionActivityInterimContext extends IKnownActivityContext<'IfCondition'> {
    Expression?: string;
    TrueChildActivities?: PipelineActivityInterimContext[];
    FalseChildActivities?: PipelineActivityInterimContext[];
}

export interface ISwitchCaseInterimContext {
    ExpressionReturnValue?: string;
    ChildActivities?: PipelineActivityInterimContext[];
}

export interface ISwitchActivityInterimContext extends IKnownActivityContext<'Switch'> {
    Expression?: string;
    Cases?: ISwitchCaseInterimContext[];
    DefaultChildActivities?: PipelineActivityInterimContext[];
}

export interface IUntilActivityInterimContext extends IKnownActivityContext<'Until'> {
    StopExpression?: string;
    Timeout?: string;
    ChildActivities?: PipelineActivityInterimContext[];
}

export type PipelineActivityInterimContext =
    | PipelineActivityContextBase
    | IForEachActivityInterimContext
    | IIfConditionActivityInterimContext
    | ISwitchActivityInterimContext
    | IUntilActivityInterimContext;
// #endregion


export type CopilotEntityProperties = {
    fabricArtifactType: TridentGetPipelineCopilotResponseParams['fabricArtifactType'];
    fabricArtifactObjectId: string;
    fabricWorkspaceObjectId: string;
}

/**
 * @see https://msdata.visualstudio.com/DefaultCollection/Azure%20Data%20Factory/_git/Trident-DI-Extension (continued in the next line)
 * @see ?path=/packages/di-extension/src/containers/Copilot/Copilot.types.ts&version=GBmain&line=205&lineEnd=205&lineStartColumn=1&lineEndColumn=34&lineStyle=plain&_a=contents
 */
export interface ICopilotUIItem {
    skill: PipelineCopilotSkill;
    copilotResponse: IPipelineCopilotResponseCore;
    isFailed?: boolean;
    isCanceled?: boolean;
    userFriendlyMessage?: string;
    errorCode?: PipelineCopilotErrorCode;
    errorMessage?: string;
    retriable?: boolean;
    requestIdInfo?: { key: string; value: string };
}

/**
 * Created based on OpenAI message contract
 * @see https://platform.openai.com/docs/api-reference/messages/listMessages
 */
export type CopilotChatMessage = {
    role: 'user' | 'assistant' | 'system';
    id: string;
    content: string;
    details?: ICopilotUIItem & {
        feedbackSubmitted?: boolean;
        isPositiveFeedback?: boolean;
        needsAccept?: boolean;
        needsClear?: boolean;
    }
}

export enum PipelineCopilotSkill { // this is actually the action kind returned by Prose
    ExplainError = 'ExplainError',
    ExplainPipeline = 'ExplainPipeline',
    UpdatePipeline = 'UpdatePipeline',
    GenerateExpression = 'GenerateExpression',
    ExplainExpression = 'ExplainExpression',
    Unknown = 'Unknown'
}

export enum CopilotExperience {
    Chat = 'Chat',
    SummaryBar = 'SummaryBar',
    SummaryButton = 'SummaryButton',
    QuickAction = 'QuickAction'
}

export enum ActionStatus {
    Succeeded = 'Succeeded',
    Failed = 'Failed',
    Cancelled = 'Cancelled',
    InProgress = 'InProgress'
}

/**
 * Interface representing the structure of a reply output message.
 * @see https://msdata.visualstudio.com/Azure%20Data%20Factory/_git/Trident-DI-Extension (continued in the next line)
 * ?path=/packages/di-extension/src/containers/Copilot/Reply/ReplyOutput.tsx&version=GBmain&line=17&lineEnd=57&lineStartColumn=1&lineEndColumn=2&lineStyle=plain&_a=contents
 */
export interface IReplyOutputMessage {
    /**
     * message id
     */
    _id: string;

    /**
     * Related artifact ID
     */
    objectId: string;

    copilotResponse: object;
    status: ActionStatus;
    loggableDisplayMessage?: string;
    displayMarkdownMessage?: string;

    /**
     * if the message is a connection message.
     */
    isConnection?: boolean;
}
